<script lang="ts">
  /**
   * The landing page. The one screen someone sees when they type the address.
   *
   * What changed and why: this used to carry everything — the portal tiles, the
   * attention rail, health states, latency, GCP project ids and a five-column table
   * of the estate — down one long scroll. All of it was true and most of it was
   * infrastructure, so the first thing the household saw when they came home was a
   * status console. The technical detail now lives on Status and Estate, and this
   * page has one job: make it obvious and pleasant to get where you are going.
   *
   * Three rules held the redesign together:
   *
   *  · **Nothing here is a number you have to interpret.** "Everything is
   *    responding" replaces "10 live / 0 slow". The figures still exist, on Status,
   *    where someone has gone looking for them.
   *  · **The portals are the page.** They are the reason the address gets typed, so
   *    they get the space, the icons and the largest targets — not a strip under a
   *    hero that is mostly about itself.
   *  · **Never colour alone.** Every state carries a glyph and a word as well as a
   *    hue, because red and green are not separable for the person who owns this.
   *    There is no green anywhere in the estate: blue is good, amber wants you.
   *
   * ## F5 — this page is ordered, not tailored
   *
   * Everything below the greeting is a **section in a list whose order comes from
   * the reader's role**, and the list is the same list for everybody. Christo leads
   * with attention and the estate; Mandri leads with the week, the boys, and what is
   * expiring and booked. Nothing is removed for either of them — a "simple version"
   * is the same mistake as an operator dashboard, made in the opposite direction.
   * `lib/order.svelte.ts` holds the two orders and the argument for them.
   *
   * ## F11 — and on a Sunday it is ordered differently again
   *
   * The week leads whoever is reading, and non-urgent items hold until Monday. They
   * are *held*, never hidden: the count is stated and there is a button that shows
   * them anyway. A screen that quietly knew something is the one failure this estate
   * cannot afford.
   */
  import { Icon, Panel } from "@steyncd/steyn-design";
  import TheList from "../components/TheList.svelte";
  import Week from "../components/Week.svelte";
  import { lineFor } from "../lib/dailyline";
  import { estate, host, ICON, type Portal } from "../lib/estate.svelte";
  import { ORDERING_LABEL, order } from "../lib/order.svelte";
  import { router } from "../lib/router.svelte";
  import { session } from "../lib/stores.svelte";
  import { heldSentence, isSunday, splitForSunday, sunday } from "../lib/sunday.svelte";
  import { SOURCES } from "../lib/tablet.svelte";
  import { deadlinesIn, week } from "../lib/week.svelte";

  /**
   * Time-of-day greeting, in the household's own timezone rather than the browser's.
   *
   * `Africa/Johannesburg` is named explicitly: the Front Door is on a phone home
   * screen that travels, and being told "good evening" over breakfast in another
   * country is the kind of small wrongness that makes software feel careless.
   */
  const hour = Number(
    new Intl.DateTimeFormat("en-ZA", { hour: "numeric", hour12: false, timeZone: "Africa/Johannesburg" }).format(
      new Date(),
    ),
  );
  const greeting = hour < 5 ? "Still up" : hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  /**
   * The date, in the same timezone and for the same reason as the greeting.
   *
   * `03-shell-and-layout.md`'s glance page opens *greeting + date + the daily line*,
   * and the three belong together: the date is what makes the verse today's rather
   * than a decoration.
   */
  const today = new Intl.DateTimeFormat("en-ZA", {
    timeZone: "Africa/Johannesburg",
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date());

  /**
   * F10 · The daily line.
   *
   * Computed, not fetched: it comes from a curated file in this bundle, so it
   * renders on the first paint with no request and no chance of an empty gap where
   * a verse should be. The kitchen tablet shows the same line on the same day —
   * `dailyline.ts` is deterministic by the house's date precisely so the two screens
   * cannot disagree.
   */
  const verse = lineFor();

  /**
   * One sentence about the estate, in words.
   *
   * Ordered by what deserves the sentence: something wanting you outranks something
   * being slow, which outranks everything being fine. Only the top fact is stated —
   * a landing page that lists three caveats at once is a status console again.
   */
  /**
   * One clock reading for the whole page, taken once.
   *
   * The same shape the greeting and the verse above already use. A page that
   * re-derived "is it Sunday" on every keystroke in the shopping list would be
   * asking the operating system the date sixty times a minute to answer a question
   * that changes once a week.
   */
  const now = Date.now();
  const isSun = isSunday(now);

  /**
   * F11 · what holds until Monday, and what could not.
   *
   * Both halves are kept. `split.show` is what the rail draws; `split.held.length`
   * is the sentence under it. On every other day `split.show` is the whole feed and
   * `held` is empty, so nothing below has to ask what day it is.
   */
  const split = $derived(splitForSunday(estate.attention, now));
  /** What is actually drawn — the held items too, if somebody asked to see them. */
  const shown = $derived(sunday.showHeld ? estate.attention : split.show);

  /** F7 · this week, or on a Sunday the one starting tomorrow. */
  const range = week.range(now);
  /**
   * Deadlines drawn on the week come from the **whole** feed, including anything
   * F11 is holding. A date on a calendar is reference, not a nudge: it says the
   * licence expires on Thursday rather than asking anyone to act now, and a Sunday
   * week that hid Thursday would be the opposite of showing the week ahead.
   */
  const marks = $derived(deadlinesIn(estate.attention, range.from));

  /** F5 · the sections, in this reader's order. */
  const sections = $derived(order.for(session.claims?.role ?? null, isSun));
  const ordering = $derived(order.active(session.claims?.role ?? null));

  const state = $derived.by(() => {
    if (estate.error) return { tone: "warn" as const, glyph: "alert", text: "Fabric could not be reached, so this page may be out of date." };
    if (!estate.loaded) return { tone: "quiet" as const, glyph: "clock", text: "Checking on the house…" };
    // `split.show`, not `shown`: pressing "show them anyway" is a request to see the
    // held items, not a claim that they suddenly want you today.
    const n = split.show.length;
    if (n > 0) {
      return {
        tone: "warn" as const,
        glyph: "inbox",
        text: isSun
          ? n === 1
            ? "One thing could not wait for Monday."
            : `${n} things could not wait for Monday.`
          : n === 1
            ? "One thing wants you today."
            : `${n} things want you today.`,
      };
    }
    // Sunday, with nothing urgent. The sentence names what is being held rather than
    // claiming the week is clear, and it outranks the estate-health lines below —
    // a portal answering slowly is not what a Sunday morning is about.
    if (isSun) return { tone: "quiet" as const, glyph: "moon", text: heldSentence(split.held.length) };
    if (estate.downCount > 0) {
      return {
        tone: "warn" as const,
        glyph: "alert",
        text:
          estate.downCount === 1
            ? "One portal is not responding."
            : `${estate.downCount} portals are not responding.`,
      };
    }
    if (estate.slowCount > 0) {
      return { tone: "warn" as const, glyph: "clock", text: "Everything is up, one portal is answering slowly." };
    }
    return { tone: "ok" as const, glyph: "check", text: "Everything is responding. Nothing needs you." };
  });
</script>

<div class="wrap">
  <!-- ── Welcome ─────────────────────────────────────────────────────────── -->
  <section class="hero">
    <div class="hero__kicker">The Steyn household · {today}</div>
    <h1 class="hero__h">{greeting}, {session.firstName}.</h1>

    <!-- The daily line. Quiet, serif, and deliberately not in a card: it is not a
         panel of data and giving it a border would make it look like one. -->
    <blockquote class="verse">
      <p class="verse__t">{verse.text}</p>
      <cite class="verse__ref">{verse.ref}</cite>
    </blockquote>

    <!-- The whole of the old three-figure row, in a sentence. Glyph + word +
         colour, and the colour is never the only signal. -->
    <p class="line line--{state.tone}">
      <span class="line__badge" aria-hidden="true"><Icon name={state.glyph} size={13} /></span>
      {state.text}
    </p>

    {#if estate.error}
      <button class="btn-primary hero__retry" onclick={() => estate.load()}>Try again</button>
    {/if}
  </section>

  <!-- ── The sections, in this reader's order ─────────────────────────────────
       F5. Every section below exists for every reader; only the order changes, and
       it is a *default* — the control in the footer switches it, which is the only
       way either person can see what the other one lands on. Rendered through an
       explicit branch rather than a lookup table so a section that is added and not
       placed in `SectionId` fails to compile instead of failing to appear. -->
  {#each sections as id (id)}
    {#if id === "wants"}
      {@render sWants()}
    {:else if id === "week"}
      {@render sWeek()}
    {:else if id === "boys"}
      {@render sBoys()}
    {:else if id === "list"}
      {@render sList()}
    {:else}
      {@render sPortals()}
    {/if}
  {/each}

  {#if estate.loading && !estate.loaded}
    <p class="sub loadingline">Loading the household…</p>
  {/if}

  <footer class="foot">
    <button class="lnk" onclick={() => router.go("status")}>How everything is running</button>
    <span aria-hidden="true">·</span>
    <button class="lnk" onclick={() => router.go("estate")}>The estate map</button>
    <span aria-hidden="true">·</span>
    <!-- Two screens that are not in the rail, and are not in it on purpose: one is a
         document and one is an object on a wall. This is where somebody finds them. -->
    <button class="lnk" onclick={() => router.go("handbook")}>The estate on one printable page</button>
    <span aria-hidden="true">·</span>
    <a class="lnk" href="#/tablet">The kitchen tablet</a>
  </footer>

  <!-- ── Whose order is this? ────────────────────────────────────────────────
       F5's *default* made visible. Two reasons this is not a hidden constant: a
       `display` or `guest` account is neither of these people, and without it
       neither person can ever look at what the other one lands on — a feature whose
       whole premise is "her page is not your page" that can only be seen from one
       side cannot be reviewed or fixed. Kept quiet and last: it is about the page
       rather than about the house. -->
  <div class="ord">
    <span class="ord__l">{ORDERING_LABEL[ordering]}</span>
    <button class="lnk" onclick={() => order.set(ordering === "owner" ? "partner" : "owner")}>
      {ordering === "owner" ? "See the household order" : "See the operator order"}
    </button>
    {#if order.chosen !== null}
      <button class="lnk" onclick={() => order.set(null)}>Back to my own</button>
    {/if}
  </div>
</div>

<!-- ── Wants you ─────────────────────────────────────────────────────────────
     Kept on the landing page on purpose. It is the only thing here that is
     time-sensitive, and burying a licence disc that expires on Friday behind a menu
     item would defeat the reason the estate posts attention at all. -->
{#snippet sWants()}
  {#if estate.attention.length > 0}
    <section class="sec">
      <h2 class="sec__h">Wants you</h2>
      <div class="wants">
        {#each shown.slice(0, 3) as a (a.id)}
          {@const urgent = a.severity === "urgent" || a.overdue}
          {@const info = a.severity === "info" && !a.overdue}
          <article class="want" class:want--urgent={urgent} class:want--info={info}>
            <span class="want__badge" class:want__badge--info={info} aria-hidden="true">
              <Icon name={info ? "dot" : urgent ? "alert" : "clock"} size={13} />
            </span>
            <div class="want__body">
              <span class="want__label">{a.overdue ? "Overdue" : urgent ? "Urgent" : info ? "Note" : "Soon"}</span>
              <p class="want__text">{a.text}</p>
              {#if a.digestCount >= 3}
                <p class="want__nag">Mentioned {a.digestCount} mornings running.</p>
              {/if}
            </div>
            <div class="want__acts">
              <a class="btn-primary" href={a.deepLink}>Open</a>
              <button class="lnk" onclick={() => estate.snooze(a.id)}>Not this week</button>
            </div>
            <button
              class="want__x"
              onclick={() => estate.dismiss(a.id)}
              aria-label={a.dueAt ? "Remind me later" : "Dismiss"}
            >
              <Icon name="x" size={14} />
            </button>
          </article>
        {/each}
      </div>
      {#if shown.length > 3}
        <p class="sec__more">
          and {shown.length - 3} more — the morning digest carries the rest.
        </p>
      {/if}
      {#if isSun && split.held.length > 0}
        <!-- F11, said out loud. Holding is not hiding: the count is stated and the
             button is a real button, because a rail that silently emptied itself one
             day a week is a rail nobody would trust the other six. -->
        <p class="sec__more held">
          <span><Icon name="moon" size={13} /> {heldSentence(split.held.length)}</span>
          <button class="lnk" onclick={() => (sunday.showHeld = !sunday.showHeld)}>
            {sunday.showHeld ? "Hold them again" : "Show them anyway"}
          </button>
        </p>
      {/if}
    </section>
  {/if}
{/snippet}

<!-- ── The week ──────────────────────────────────────────────────────────────
     F7. Empty of entries, and honestly so — see `lib/week.svelte.ts`. The deadlines
     under it are real, which is what makes this section worth its place on the page
     today rather than only after a calendar is wired. -->
{#snippet sWeek()}
  <section class="sec">
    <h2 class="sec__h">{range.label}</h2>
    <Week {now} {marks} marksKnown={estate.attentionReadable} />
  </section>
{/snippet}

<!-- ── The boys ──────────────────────────────────────────────────────────────
     F6's balances, which do not exist yet. The same two named slots the kitchen
     tablet carries, in the same words: two screens explaining one missing source
     differently is how a household learns the explanations are decoration. -->
{#snippet sBoys()}
  <section class="sec">
    <h2 class="sec__h">{SOURCES.chores.title}</h2>
    <div class="boys">
      <div class="boy"><span class="boy__fig">—</span><span class="boy__l">Liam · balance</span></div>
      <div class="boy"><span class="boy__fig">—</span><span class="boy__l">Eben · balance</span></div>
    </div>
    <Panel isEmpty>
      {#snippet empty()}
        {SOURCES.chores.why}
        <span class="next">{SOURCES.chores.next}</span>
      {/snippet}
      {#snippet children()}{/snippet}
    </Panel>
  </section>
{/snippet}

<!-- ── The list ──────────────────────────────────────────────────────────────
     F8. The only section on this page that writes anything. -->
{#snippet sList()}
  <section class="sec">
    <h2 class="sec__h">The list</h2>
    <TheList />
  </section>
{/snippet}

<!-- ── The estate ────────────────────────────────────────────────────────────
     Three blocks that are one section: what the house already runs, what is being
     built for it, and what is not open yet. They move together because splitting
     them across an ordering would put "still being built" above the week for one
     reader and below the list for the other, which is a shuffle rather than an
     order. -->
{#snippet sPortals()}
  {#if estate.house.length > 0}
    <section class="sec">
      <h2 class="sec__h">The house</h2>
      <div class="grid">
        {#each estate.house as p (p.id)}{@render tile(p)}{/each}
      </div>
    </section>
  {/if}

  {#if estate.programme.length > 0}
    <section class="sec">
      <h2 class="sec__h">Built for the house</h2>
      <div class="grid">
        {#each estate.programme as p (p.id)}{@render tile(p)}{/each}
      </div>
    </section>
  {/if}

  <!-- Still-being-built portals are listed rather than tiled: a full-size card for
       something you cannot open yet promises a door where there is a wall. -->
  {#if estate.coming.length > 0}
    <section class="sec">
      <h2 class="sec__h">Still being built</h2>
      <div class="coming">
        {#each estate.coming as p (p.id)}
          <span class="chip">
            <Icon name={ICON[p.id] ?? "door"} size={14} />
            {p.name}
            <span class="chip__state">{p.status}</span>
          </span>
        {/each}
      </div>
    </section>
  {/if}
{/snippet}

{#snippet tile(p: Portal)}
  <!-- The whole card is the target. A card with a small "Open" button inside it
       spends its size on decoration and then asks for precision anyway. -->
  <a class="tile" href={p.url}>
    <span class="tile__icon" aria-hidden="true"><Icon name={ICON[p.id] ?? "door"} size={20} /></span>
    <span class="tile__name">{p.name}</span>
    <span class="tile__blurb">{p.blurb}</span>
    <span class="tile__foot">
      <span class="tile__host num">{host(p.url)}</span>
      <!-- Only ever shown when something is wrong. A green tick beside all ten
           portals is noise that trains people to stop reading the dots; the absence
           of a mark is the good news, and the sentence at the top says so. -->
      {#if p.health && !p.health.ok}
        <span class="tile__flag"><Icon name="alert" size={11} /> not responding</span>
      {:else if p.health?.slow}
        <span class="tile__flag"><Icon name="clock" size={11} /> slow</span>
      {/if}
    </span>
  </a>
{/snippet}

<style>
  .wrap {
    display: flex;
    flex-direction: column;
    gap: 30px;
    padding-bottom: 40px;
  }

  /* ── hero ───────────────────────────────────────────────────────────────
     No card. The old hero was a glass panel sitting on the aurora, which framed
     the greeting as another widget among widgets; letting it sit directly on the
     background is what makes the page read as a welcome rather than a dashboard. */
  .hero {
    padding: 30px 0 4px;
  }
  .hero__kicker {
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.7px;
    text-transform: uppercase;
    color: var(--mut);
  }
  .hero__h {
    margin: 8px 0 0;
    font-size: clamp(27px, 4.4vw, 40px);
    font-weight: 800;
    letter-spacing: -1.1px;
    line-height: 1.08;
    color: var(--tx);
  }
  /* The daily line. Serif and quiet — the surface's own figure/heading face, since
     this is the one piece of prose on the page that is meant to be read slowly. It
     carries no border and no glyph because it is not a status: it is the same every
     time you look, which is the point of it. */
  .verse {
    margin: 14px 0 0;
    max-width: 56ch;
  }
  .verse__t {
    margin: 0;
    font-family: var(--font-serif);
    font-size: 1.0625rem;
    line-height: 1.45;
    color: var(--tx2);
  }
  .verse__ref {
    display: block;
    margin-top: 5px;
    font-size: var(--fs-micro);
    font-weight: 700;
    font-style: normal;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: var(--mut);
  }

  .line {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    margin: 18px 0 0;
    padding: 7px 14px 7px 9px;
    border-radius: var(--r-pill);
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--tx2);
    background: var(--fill);
    box-shadow: inset 0 0 0 1px var(--line);
  }
  .line__badge {
    display: grid;
    place-items: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    flex-shrink: 0;
    color: var(--bg);
  }
  /* Blue is good and amber wants you. There is deliberately no green token in this
     estate at all — red/green is exactly the axis its owner cannot separate. */
  .line--ok .line__badge {
    background: var(--ok);
  }
  .line--warn .line__badge {
    background: var(--warn);
  }
  .line--quiet .line__badge {
    background: var(--mut);
  }
  .hero__retry {
    margin: 14px 0 0;
    padding: 8px 16px;
    font-size: 0.7812rem;
  }

  /* ── sections ──────────────────────────────────────────────────────────── */
  .sec__h {
    margin: 0 0 12px;
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: var(--mut);
  }
  .sec__more,
  .loadingline {
    margin: 10px 0 0;
    font-size: 0.75rem;
    color: var(--mut);
  }

  /* ── portal grid ───────────────────────────────────────────────────────── */
  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(232px, 1fr));
    gap: 12px;
  }
  .tile {
    display: grid;
    grid-template-rows: auto auto 1fr auto;
    gap: 7px;
    padding: 17px 17px 14px;
    border-radius: var(--r-card, 14px);
    background: var(--s1);
    box-shadow: inset 0 0 0 1px var(--line);
    color: var(--tx);
    transition:
      transform var(--dur) var(--ease),
      box-shadow var(--dur) var(--ease),
      background var(--dur) var(--ease);
  }
  .tile:hover {
    background: var(--card-hover);
    box-shadow: inset 0 0 0 1px var(--acc);
    transform: translateY(-2px);
  }
  .tile:focus-visible {
    outline: 2px solid var(--acc);
    outline-offset: 2px;
  }
  .tile__icon {
    display: grid;
    place-items: center;
    width: 38px;
    height: 38px;
    border-radius: 11px;
    background: var(--grad);
    /* The glyph sits ON the copper gradient, so it takes the page background
       colour — an accent glyph on an accent fill is invisible. */
    color: var(--bg);
  }
  .tile__name {
    font-size: 0.9688rem;
    font-weight: 800;
    letter-spacing: -0.3px;
  }
  .tile__blurb {
    font-size: 0.7812rem;
    line-height: 1.45;
    color: var(--mut);
  }
  .tile__foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 4px;
    padding-top: 10px;
    border-top: 1px solid var(--line);
  }
  .tile__host {
    font-size: 0.72rem;
    color: var(--mut);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .tile__flag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.72rem;
    font-weight: 800;
    color: var(--warn);
    white-space: nowrap;
  }

  /* ── wants you ─────────────────────────────────────────────────────────── */
  .wants {
    display: flex;
    flex-direction: column;
    gap: 9px;
  }
  .want {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 15px;
    border-radius: var(--r-card, 14px);
    background: var(--s1);
    box-shadow: inset 0 0 0 1px var(--line);
    border-left: 3px solid var(--warn);
  }
  .want--info {
    border-left-color: var(--ok);
  }
  .want--urgent {
    /* A second amber, darker, so urgent and soon are separable by luminance and
       not only by the word — deliberately not equal to --warn. */
    border-left-color: var(--error, var(--warn));
  }
  .want__badge {
    display: grid;
    place-items: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: var(--warn);
    color: var(--bg);
    flex-shrink: 0;
  }
  .want__badge--info {
    background: var(--ok);
  }
  .want__body {
    flex: 1;
    min-width: 0;
  }
  .want__label {
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: var(--mut);
  }
  .want__text {
    margin: 3px 0 0;
    font-size: 0.8125rem;
    line-height: 1.45;
    color: var(--tx);
  }
  .want__nag {
    margin: 5px 0 0;
    font-size: 0.72rem;
    color: var(--mut);
  }
  .want__acts {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }
  .want__x {
    padding: 5px;
    border-radius: var(--r-control);
    color: var(--mut);
    flex-shrink: 0;
  }
  .want__x:hover {
    color: var(--acc);
  }

  /* ── still building ────────────────────────────────────────────────────── */
  .coming {
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
  }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 7px 12px;
    border-radius: var(--r-pill);
    font-size: 0.75rem;
    font-weight: 700;
    color: var(--mut);
    box-shadow: inset 0 0 0 1px var(--line);
  }
  .chip__state {
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    opacity: 0.75;
  }

  .foot {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 6px;
    padding-top: 18px;
    border-top: 1px solid var(--line);
    font-size: 0.75rem;
    color: var(--mut);
  }
  /* One of these four is an anchor and three are buttons — the tablet is a real
     link because it gets bookmarked and opened in a kiosk, the rest are in-app
     navigation. Scoped to the footer so the anchor stops inheriting the global link
     colour and the four read as one row rather than three words and a link. */
  .foot .lnk {
    color: var(--mut);
    font-weight: 600;
  }
  .foot .lnk:hover {
    color: var(--acc);
  }

  /* ── the boys (F6, unwired) ─────────────────────────────────────────────
     Two named slots rather than one, because the shape of what is missing is itself
     information: a list each and a balance each, not one shared list. The same two
     slots the kitchen tablet carries. */
  .boys {
    display: flex;
    gap: 34px;
    margin-bottom: 12px;
  }
  .boy {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }
  /* An em dash standing in for a figure that has no source. Muted rather than
     full-strength text: it is not a reading and should not read like one. */
  .boy__fig {
    font-family: var(--font-figure);
    font-size: 1.75rem;
    font-weight: 700;
    line-height: 1;
    color: var(--mut);
  }
  .boy__l {
    font-size: var(--fs-micro);
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: var(--mut);
  }
  /* The next action inside an empty state. A block, so it never reads as part of the
     sentence explaining why the panel is empty. */
  .next {
    display: block;
    margin-top: 5px;
    font-weight: 700;
    color: var(--tx2);
  }

  /* ── F11's held line ────────────────────────────────────────────────────── */
  .held {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 12px;
  }
  .held span {
    display: inline-flex;
    align-items: center;
    gap: 7px;
  }

  /* ── F5's ordering control ──────────────────────────────────────────────── */
  .ord {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    font-size: var(--fs-micro);
    color: var(--mut);
  }
  .ord__l {
    font-weight: 800;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
  .ord .lnk {
    color: var(--mut);
    font-weight: 600;
  }
  .ord .lnk:hover {
    color: var(--acc);
  }

  @media (max-width: 620px) {
    .want {
      flex-wrap: wrap;
    }
    .want__acts {
      width: 100%;
      justify-content: flex-start;
      padding-left: 36px;
    }
  }
</style>
